import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Badge,
	Box,
	Button,
	FormControl,
	HStack,
	Select,
	SimpleGrid,
	Text,
	Textarea,
	useColorModeValue,
	VStack,
} from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiCheckCircle, FiClock, FiEdit3, FiFileText, FiMessageSquare, FiUser } from "react-icons/fi";

import {
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalSection,
	PropertyModalShell,
} from "../PropertyModal/shared";
import {
	addAgencyTaskMessageAsync,
	cancelAgencyTaskAsync,
	clearSelectedTask,
	getAgencyTaskAsync,
	updateAgencyTaskAsync,
} from "../../redux/tasks/tasksSlice";
import { fullDateFormat, fullDateTimeFormat } from "../../utils/types";

const STATUS_OPTIONS = ["open", "inProgress", "done", "cancelled"];

function statusColor(status) {
	switch (status) {
		case "open":
			return "red";
		case "inProgress":
			return "yellow";
		case "done":
			return "green";
		case "cancelled":
			return "gray";
		default:
			return "blue";
	}
}

function isClosedTask(task) {
	return task?.status === "done" || task?.status === "cancelled";
}

function isOverdue(task) {
	if (!task?.dueDate || isClosedTask(task)) return false;
	return moment(task.dueDate, "YYYY-MM-DD").isBefore(moment(), "day");
}

function isDueSoon(task) {
	if (!task?.dueDate || isClosedTask(task) || isOverdue(task)) return false;
	const due = moment(task.dueDate, "YYYY-MM-DD");
	const today = moment().startOf("day");
	const daysUntilDue = due.diff(today, "days");
	return daysUntilDue >= 0 && daysUntilDue <= 7;
}

function dueDateUrgency(task) {
	if (isOverdue(task)) return "overdue";
	if (isDueSoon(task)) return "soon";
	return null;
}

function DueDateBadge({ task, showDate = true }) {
	const { t } = useTranslation();
	const urgency = dueDateUrgency(task);
	const label = showDate && task?.dueDate ? moment(task.dueDate).format(fullDateFormat) : null;

	if (urgency === "overdue") {
		return (
			<Badge colorScheme="red" variant="subtle">
				{label ? `${label} · ${t("tasks.overdue")}` : t("tasks.overdue")}
			</Badge>
		);
	}
	if (urgency === "soon") {
		return (
			<Badge colorScheme="yellow" variant="subtle">
				{label ? `${label} · ${t("tasks.dueSoon")}` : t("tasks.dueSoon")}
			</Badge>
		);
	}
	if (label) {
		return (
			<Badge colorScheme="gray" variant="subtle">
				{label}
			</Badge>
		);
	}
	return null;
}

export default function TaskDetailModal({ isOpen, onClose, taskId, onEdit }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const task = useSelector((state) => state.tasks.selectedTask);
	const detailStatus = useSelector((state) => state.tasks.detailStatus);
	const mutationStatus = useSelector((state) => state.tasks.mutationStatus);
	const [message, setMessage] = useState("");
	const descriptionBg = useColorModeValue("gray.50", "gray.700");

	useEffect(() => {
		if (isOpen && taskId) {
			dispatch(getAgencyTaskAsync(taskId));
		}
		if (!isOpen) {
			dispatch(clearSelectedTask());
			setMessage("");
		}
	}, [isOpen, taskId, dispatch]);

	const handleStatusChange = (e) => {
		if (!task) return;
		const status = e.target.value;
		if (status === "cancelled") {
			dispatch(cancelAgencyTaskAsync(task.id));
			return;
		}
		dispatch(updateAgencyTaskAsync({ taskId: task.id, status }));
	};

	const submitMessage = () => {
		const text = message.trim();
		if (!text || !task) return;
		dispatch(addAgencyTaskMessageAsync({ taskId: task.id, text })).then((result) => {
			if (!result.error) setMessage("");
		});
	};

	return (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="3xl"
			icon={FiCheckCircle}
			title={task?.title || t("tasks.taskDetails")}
			subtitle={t("tasks.taskDetailSubtitle")}
			footer={<ModalFooterActions onCancel={onClose} cancelLabel={t("misc.close")} isLoading={mutationStatus === "loading"} />}
		>
			{detailStatus === "loading" && !task ? (
				<Text color="gray.500">{t("misc.loading")}</Text>
			) : task ? (
				<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
					<PropertyModalSection icon={FiFileText} title={t("tasks.taskDetails")}>
						<VStack spacing={3} align="stretch">
							<HStack justify="space-between" flexWrap="wrap">
								<HStack spacing={2}>
									<Badge colorScheme={statusColor(task.status)}>{t(`tasks.statusValues.${task.status}`)}</Badge>
									{dueDateUrgency(task) === "overdue" && <Badge colorScheme="red">{t("tasks.overdue")}</Badge>}
									{dueDateUrgency(task) === "soon" && <Badge colorScheme="yellow">{t("tasks.dueSoon")}</Badge>}
								</HStack>
								<Button size="sm" leftIcon={<FiEdit3 />} variant="outline" onClick={() => onEdit?.(task)}>
									{t("misc.edit")}
								</Button>
							</HStack>
							<SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
								<HStack>
									<FiUser />
									<Text fontSize="sm">
										{t("tasks.assignee")}: {task.assignee}
									</Text>
								</HStack>
								<HStack>
									<FiCalendar />
									<Text fontSize="sm">{t("tasks.dueDate")}:</Text>
									<DueDateBadge task={task} />
								</HStack>
								<HStack>
									<FiClock />
									<Text fontSize="sm">
										{t("tasks.createdAt")}: {moment(task.createdAt).format(fullDateTimeFormat)}
									</Text>
								</HStack>
							</SimpleGrid>
							<Box bg={descriptionBg} p={3} borderRadius="md">
								<Text whiteSpace="pre-wrap">{task.description || t("tasks.noDescription")}</Text>
							</Box>
							<FormControl maxW="240px">
								<ModalFieldLabel icon={FiCheckCircle}>{t("tasks.status")}</ModalFieldLabel>
								<Select size="sm" value={task.status} onChange={handleStatusChange}>
									{STATUS_OPTIONS.map((status) => (
										<option key={status} value={status}>
											{t(`tasks.statusValues.${status}`)}
										</option>
									))}
								</Select>
							</FormControl>
						</VStack>
					</PropertyModalSection>

					<PropertyModalSection icon={FiMessageSquare} title={t("tasks.progressThread")}>
						<VStack spacing={3} align="stretch">
							{(task.messages || []).length === 0 ? (
								<Text color="gray.500">{t("tasks.noMessages")}</Text>
							) : (
								(task.messages || []).map((entry) => (
									<Box key={entry.id} borderWidth="1px" borderRadius="md" p={3}>
										<HStack justify="space-between" mb={1}>
											<Text fontWeight="semibold" fontSize="sm">
												{entry.author?.name || entry.author?.email || "-"}
											</Text>
											<Text fontSize="xs" color="gray.500">
												{moment(entry.createdAt).format(fullDateTimeFormat)}
											</Text>
										</HStack>
										<Text whiteSpace="pre-wrap">{entry.text}</Text>
									</Box>
								))
							)}
							<FormControl>
								<ModalFieldLabel icon={FiMessageSquare}>{t("tasks.addMessage")}</ModalFieldLabel>
								<Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} borderRadius="md" />
							</FormControl>
							<Button
								alignSelf="flex-end"
								colorScheme="blue"
								onClick={submitMessage}
								isLoading={mutationStatus === "loading"}
								isDisabled={!message.trim()}
							>
								{t("tasks.postMessage")}
							</Button>
						</VStack>
					</PropertyModalSection>
				</VStack>
			) : (
				<Text color="gray.500">{t("table.noData")}</Text>
			)}
		</PropertyModalShell>
	);
}

export { isOverdue, isDueSoon, dueDateUrgency, DueDateBadge, statusColor };
